import { useFindBy, useGlobalAction } from "@gadgetinc/react";
import { Datepicker } from "flowbite-react";
import { useCallback, useContext, useEffect, useState } from "react";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import StatisticsCards from "../../externalComponents/StatisticsCards";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../externalComponents/tailwind/table";
import { Text } from "../../externalComponents/tailwind/text";
import { useIsMobile } from "../../externalComponents/useIsMobile";
import { CustomerContext } from "../../providers";
import { calculateRate } from "../../functions/calculateRate";

export default function StatisticsPage() {
  const { user, integrations, shop } = useContext(CustomerContext);
  const [dataStats, setDataStats] = useState(null);
  const isMobile = useIsMobile();

  const [selectedDateFilter, setSelectedDateFilter] = useState("30days");
  const today = new Date();
  const todayAt23h59 = new Date();
  todayAt23h59.setHours(23, 59, 59, 999);
  const [startDate, setStartDate] = useState(() => {
    const newDate = new Date(today);
    newDate.setDate(newDate.getDate() - 30);
    return newDate;
  });
  const [endDate, setEndDate] = useState(todayAt23h59);

  const [, setStartDateExport] = useState(startDate);
  const [, setEndDateExport] = useState(endDate);

  const [{ data }] = useFindBy(
    api.customerStats.findByCustomer,
    user.customerId,
    {
      select:
        selectedDateFilter === "Between"
          ? { id: true }
          : {
              ["sent" + selectedDateFilter]: true,
              ["delivered" + selectedDateFilter]: true,
              ["read" + selectedDateFilter]: true,
              ["optins" + selectedDateFilter]: true,
              ["turnover" + selectedDateFilter]: true,
              ["externalTurnover" + selectedDateFilter]: true,
              ["sales" + selectedDateFilter]: true,
              ["messagesCount" + selectedDateFilter]: true,
              ["optoutCount" + selectedDateFilter]: true,
              ["totalWhatsappCostInEuros" + selectedDateFilter]: true,
            },
    },
  );

  useEffect(() => {
    setDataStats(null);
    const updateDataStats = async () => {
      if (selectedDateFilter === "Between") {
        const customerDataBetweenTemp = await api.getCustomerStatsBetweenDates({
          shopId: shop?.id,
          startDate: startDate,
          endDate: endDate,
        });
        setDataStats(customerDataBetweenTemp);
      } else {
        setDataStats(
          data
            ? {
                ...data,
                totalWhatsappCostInEuros:
                  data[`totalWhatsappCostInEuros${selectedDateFilter}`],
              }
            : null,
        );
      }
    };

    updateDataStats();
  }, [selectedDateFilter, startDate, endDate, user, data]);

  const handleSelectChange = useCallback((value) => {
    setSelectedDateFilter(value);
  }, []);
  const options = [
    { label: "Today", value: "Today" },
    { label: "This week", value: "ThisWeek" },
    { label: "This month", value: "ThisMonth" },
    { label: "Last 7 days", value: "7days" },
    { label: "Last month", value: "LastMonth" },
    { label: "Last 30 days", value: "30days" },
    { label: "Last 90 days", value: "90days" },
    { label: "This year", value: "ThisYear" },
    { label: "Last year", value: "LastYear" },
    { label: "Between", value: "Between" },
  ];

  const customTheme = {
    popup: {
      root: {
        base: isMobile
          ? "fixed left-1/2 top-1/2 z-90 block pt-2 w-[90vw] max-w-xs -translate-x-1/2 -translate-y-1/2"
          : "absolute top-10 z-50 block pt-2 w-[22em]",
        inner:
          "inline-block rounded-customBorderRadius bg-white p-4 border w-full mb-5 dark:bg-gray-800",
        input: {
          field: {
            input: {
              base: "hover:border-yellow ring-purple focus:ring-transparent",
            },
          },
        },
      },
    },
    views: {
      days: {
        items: {
          base: "grid w-full grid-cols-7",
          item: {
            base: "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
            selected: "bg-yellow hover:bg-yellow",
            disabled: "text-gray-400",
          },
        },
      },
    },
  };

  const handleDateChange = (date, type, isExport) => {
    if (type === "start") {
      if (isExport) {
        setStartDateExport(date);
      } else {
        setStartDate(date);
        setStartDateExport(date);
      }
    } else if (type === "end") {
      if (isExport) {
        setEndDateExport(date);
      } else {
        const adjustedEndDate = new Date(date);
        adjustedEndDate.setHours(23, 59, 59, 999);
        setEndDate(adjustedEndDate);
        setEndDateExport(adjustedEndDate);
      }
    }
  };

  const [customerStats, getCustomerStats] = useGlobalAction(
    api.clickhouse.messages.getAnalytics,
    {
      period: selectedDateFilter,
      between: {
        fromDate: startDate,
        toDate: endDate,
      },
    },
  );
  const { data: customerStatsData } = customerStats;

  console.log("customerStatsData", customerStatsData);

  useEffect(() => {
    console.log("fetching customer stats start");
    getCustomerStats({
      period: selectedDateFilter,
      between: {
        fromDate: startDate,
        toDate: endDate,
      },
    });
    console.log("fetching customer stats end");
  }, [selectedDateFilter, startDate, endDate]);

  return (
    <>
      <PageHeading
        title="Analytics"
        select={true}
        selectComponents={{
          options: options,
          selected: selectedDateFilter,
          onChange: (value) => handleSelectChange(value),
          value: selectedDateFilter,
        }}
      />

      <OverflowFrame>
        {selectedDateFilter === "Between" && (
          <div className="flex mt-4 mx-3 md-mx-0">
            <div className="w-[22em]">
              <Datepicker
                theme={customTheme}
                maxDate={today}
                value={startDate.toLocaleDateString()}
                defaultDate={startDate}
                onSelectedDateChanged={(date) =>
                  handleDateChange(date, "start", false)
                }
                weekStart={1}
                showClearButton={false}
                showTodayButton={false}
              />
            </div>
            <div className="w-[22em] ml-3">
              <Datepicker
                theme={customTheme}
                maxDate={today}
                minDate={startDate}
                value={endDate.toLocaleDateString()}
                onSelectedDateChanged={(date) =>
                  handleDateChange(date, "end", false)
                }
                weekStart={1}
                showClearButton={false}
                showTodayButton={false}
              />
            </div>
          </div>
        )}

        <PageMarkup
          data={dataStats}
          user={user}
          integrations={integrations}
          selectedDateFilter={selectedDateFilter}
          startDate={startDate}
          endDate={endDate}
          isMobile={isMobile}
        />
      </OverflowFrame>
    </>
  );
}

function PageMarkup({
  data,
  user,
  integrations,
  selectedDateFilter,
  startDate,
  endDate,
  isMobile,
}) {
  const [fetchingCampaigns, setFetchingCampaigns] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    // Skip campaign queries when in Between mode
    if (selectedDateFilter === "Between") {
      setCampaigns([]);
      setAutomations([]);
      setFetchingCampaigns(false);
      return;
    }

    async function fetchAllCampaigns() {
      setFetchingCampaigns(true);

      try {
        const { campaigns, automations } = await api.getAllCampaigns({
          selectedDateFilter: selectedDateFilter,
          endDate: endDate,
          onlyWithMessage: true,
        });

        setCampaigns(campaigns || []);
        setAutomations(automations || []);
      } catch (error) {
        console.error("Erreur lors de la récupération des campagnes :", error);
      }

      setFetchingCampaigns(false);
    }

    fetchAllCampaigns();
  }, [selectedDateFilter, user.customer.id, endDate]);

  let timeFilterSuffix = selectedDateFilter;

  let headings = ["Name", "Sent", "Open rate", "Optout rate"];
  if (isMobile) {
    headings = ["Name", "Sent", "ARPC"];
  } else {
    if (integrations.splio || integrations.shopify) {
      headings.splice(3, 0, "Conversion rate");
      headings.splice(3, 0, "ARPC");
      headings.splice(3, 0, "Turnover");
    }
    if (integrations.shopify) {
      headings.splice(3, 0, "Sales");
    }
  }

  const newOptins = data?.[`optins${timeFilterSuffix}`] || "-";

  const sentRate = data
    ? calculateRate(
        data[`sent${timeFilterSuffix}`],
        data[`messagesCount${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const deliveredRate = data
    ? calculateRate(
        data[`delivered${timeFilterSuffix}`],
        data[`sent${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const openRate = data
    ? calculateRate(
        data[`read${timeFilterSuffix}`],
        data[`delivered${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const failedRate = data
    ? calculateRate(
        data[`messagesCount${timeFilterSuffix}`] -
          data[`sent${timeFilterSuffix}`],
        data[`messagesCount${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const optoutRate = data
    ? calculateRate(
        data[`optoutCount${timeFilterSuffix}`],
        data[`sent${timeFilterSuffix}`],
      ).toFixed(1)
    : null;

  const turnover =
    user.customer.isExternalTurnoverSplioActivated &&
    data?.[`externalTurnover${timeFilterSuffix}`]
      ? data[`externalTurnover${timeFilterSuffix}`].toFixed(0)
      : integrations.shopify && data?.[`turnover${timeFilterSuffix}`]
        ? data[`turnover${timeFilterSuffix}`].toFixed(0)
        : 0;

  const conversionRate = (
    Math.min(
      1,
      data?.[`sales${timeFilterSuffix}`] /
        Math.max(1, data?.[`messagesCount${timeFilterSuffix}`]),
    ) * 100
  )?.toFixed(1);

  const revenueStats = [
    {
      name: "Sales",
      tooltip: true,
      tooltipContent: "Number of sales attributed to WhatsApp.",
      value: !data
        ? null
        : data[`sales${timeFilterSuffix}`] > 0
          ? data[`sales${timeFilterSuffix}`]
          : "0",
    },
    {
      name: "Turnover",
      tooltip: true,
      tooltipContent: "The total revenue attributed to WhatsApp.",
      value: !data ? null : turnover ? `${turnover}€` : "-",
    },
    {
      name: "WhatsApp cost",
      tooltip: true,
      tooltipContent:
        "The WhatsApp cost is available for messages sent after May 17th, 2025.",
      value: !data
        ? null
        : data[`totalWhatsappCostInEuros${timeFilterSuffix}`] > 0
          ? `${(data[`totalWhatsappCostInEuros${timeFilterSuffix}`] || 0).toFixed(2)}€`
          : "0€",
    },
    {
      name: "Conversion rate",
      tooltip: true,
      tooltipContent: "Percentage of messages that resulted in a sale.",
      value: !data
        ? null
        : data[`sales${timeFilterSuffix}`] > 0
          ? conversionRate + "%"
          : "-",
    },
    {
      name: "ARPC",
      tooltip: true,
      tooltipContent:
        "Average Revenue Per Conversation: turnover divided by sent messages.",
      value: !data
        ? null
        : data[`turnover${timeFilterSuffix}`] && data[`sent${timeFilterSuffix}`]
          ? (
              data[`turnover${timeFilterSuffix}`] /
              data[`sent${timeFilterSuffix}`]
            ).toFixed(2) + "€"
          : data[`externalTurnover${timeFilterSuffix}`] > 0
            ? (
                data[`externalTurnover${timeFilterSuffix}`] /
                data[`delivered${timeFilterSuffix}`]
              ).toFixed(2) + "€"
            : "0€",
    },
    {
      name: "ROI",
      tooltip: true,
      tooltipContent:
        "Turnover divided by WhatsApp cost. The WhatsApp cost is available for messages sent after May 17th, 2025.",
      value: !data
        ? null
        : data[`totalWhatsappCostInEuros${timeFilterSuffix}`] > 0
          ? (
              turnover / data[`totalWhatsappCostInEuros${timeFilterSuffix}`]
            ).toFixed(2)
          : "-",
    },
  ];
  const audienceStats = [
    {
      name: "Total optins",
      value: user.customer.optinsCount,
    },
    {
      name: "New optins",
      value: !data ? null : newOptins,
    },
    {
      name: "Optouts",
      value: !data ? null : data?.[`optoutCount${timeFilterSuffix}`],
    },
    {
      name: "Optout rate",
      value: !data ? null : optoutRate > 0 ? optoutRate + "%" : "-",
      change: !data
        ? null
        : `${data?.[`optoutCount${timeFilterSuffix}`] || 0}/${
            data?.[`sent${timeFilterSuffix}`] || 0
          }`,
      // value: !data ? null : `${optoutRate}%`,
    },
  ];

  const sendingsStats = [
    {
      name: "Sent rate",
      value: !data ? null : sentRate > 0 ? sentRate + "%" : "-",
      change: !data
        ? null
        : `${data?.[`sent${timeFilterSuffix}`] || 0}/${
            data?.[`messagesCount${timeFilterSuffix}`] || 0
          }`,
    },
    {
      name: "Delivered rate",
      value: !data ? null : deliveredRate > 0 ? deliveredRate + "%" : "-",
      change: !data
        ? null
        : `${data?.[`delivered${timeFilterSuffix}`] || 0}/${
            data?.[`sent${timeFilterSuffix}`] || 0
          }`,
    },
    {
      name: "Open rate",
      value: !data ? null : openRate > 0 ? openRate + "%" : "-",
      change: !data
        ? null
        : `${data?.[`read${timeFilterSuffix}`] || 0}/${
            data?.[`delivered${timeFilterSuffix}`] || 0
          }`,
    },
    {
      name: "Failed rate",
      value: !data ? null : failedRate > 0 ? failedRate + "%" : "-",
      change: !data
        ? null
        : `${
            data?.[`messagesCount${timeFilterSuffix}`] -
              data?.[`sent${timeFilterSuffix}`] || 0
          }/${data?.[`messagesCount${timeFilterSuffix}`] || 0}`,
    },
  ];

  return (
    <>
      {(integrations.shopify || integrations.splio) && (
        <div className="border bg-greyDashboardBG rounded-customBorderRadius p-3 my-4 mx-3 md:mx-0">
          <Text className="text-xl font-bold mb-5">Revenue</Text>
          <StatisticsCards stats={revenueStats} />
        </div>
      )}

      <div className="border bg-greyDashboardBG rounded-customBorderRadius p-3 my-4 mx-3 md:mx-0">
        <Text className="text-xl font-bold mb-5">⁠Audience</Text>
        <StatisticsCards stats={audienceStats} />
      </div>
      <div className="border bg-greyDashboardBG border-pink rounded-customBorderRadius p-3 my-4 mx-3 md:mx-0">
        <Text className="text-xl font-bold mb-5">
          {`Deliverability on `}
          <span className="text-[#DF0F63] text-xl">
            {data?.[`messagesCount${timeFilterSuffix}`] || "0"}
          </span>
          {` targeted recipients`}
          {!!data?.totalWhatsappCostInEuros && (
            <>
              {" "}
              for{" "}
              <span className="text-[#DF0F63] text-xl">
                {data.totalWhatsappCostInEuros.toFixed(2) + "€"}
              </span>
            </>
          )}
        </Text>
        <StatisticsCards stats={sendingsStats} />
      </div>

      {selectedDateFilter !== "Between" && (
        <>
          <div className="flex-col border rounded-customBorderRadius p-3 mt-3 mx-3 md:mx-0">
            <Text className="text-lg font-bold">Automations</Text>
            {!fetchingCampaigns ? (
              <Table>
                <TableHead>
                  <TableRow>
                    {headings.map((heading, index) => (
                      <TableHeader
                        key={index}
                        className={index > 0 ? "text-center" : "w-[20%]"}
                      >
                        {heading}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                {automations && (
                  <TableBody>
                    <DataRows
                      data={automations}
                      selectedDateFilter={selectedDateFilter}
                      user={user}
                      startDate={startDate}
                      endDate={endDate}
                      integrations={integrations}
                      timeFilterSuffix={timeFilterSuffix}
                      isMobile={isMobile}
                    />
                  </TableBody>
                )}
              </Table>
            ) : (
              <div className="h-[50px]">
                <LoadingSpinner />
              </div>
            )}
          </div>
          <br />
          <div className="flex-col border rounded-customBorderRadius p-3 mt-3 mx-3 md:mx-0">
            <Text className="text-lg font-bold">Campaigns</Text>

            {!fetchingCampaigns ? (
              <Table>
                <TableHead>
                  <TableRow>
                    {headings.map((heading, index) => (
                      <TableHeader
                        key={index}
                        className={index > 0 ? "text-center" : "w-[20%]"}
                      >
                        {heading}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                {campaigns && (
                  <TableBody>
                    <DataRows
                      data={campaigns}
                      selectedDateFilter={selectedDateFilter}
                      user={user}
                      startDate={startDate}
                      endDate={endDate}
                      integrations={integrations}
                      timeFilterSuffix={timeFilterSuffix}
                      isMobile={isMobile}
                    />
                  </TableBody>
                )}
              </Table>
            ) : (
              <div className="h-[100px]">
                <LoadingSpinner />
              </div>
            )}
          </div>
        </>
      )}

      <br />
      <br />
    </>
  );
}

function DataRows({
  data,
  selectedDateFilter,
  user,
  startDate,
  endDate,
  integrations,
  timeFilterSuffix,
  isMobile,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      let newRows = [];

      for (const campaign of data || []) {
        let id = campaign.id;
        let name = campaign.name;
        let stats = campaign.campaignStats;

        if (
          selectedDateFilter === "Between" &&
          new Date(campaign.createdAt) <= new Date(endDate)
        ) {
          try {
            stats = await api.getCampaignStatsBetweenDates({
              campaignId: campaign.id,
              scenarioId: campaign.scenario?.id,
              startDate: startDate,
              endDate: endDate,
            });
          } catch (error) {
            console.error("Erreur lors de la récupération des stats :", error);
          }
        }

        if (stats && stats[`sent${timeFilterSuffix}`] > 0) {
          let row = (
            <TableRow key={id} href={`/analytics/${id}`}>
              <TableCell>{name}</TableCell>
              <TableCell className="text-center">
                {stats[`sent${timeFilterSuffix}`]}
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell className="text-center">
                    {(
                      Math.min(
                        1,
                        stats[`read${timeFilterSuffix}`] /
                          Math.max(1, stats[`delivered${timeFilterSuffix}`]),
                      ) * 100
                    )?.toFixed(0) + "%"}
                  </TableCell>

                  {integrations.shopify && (
                    <>
                      <TableCell className="text-center">
                        {stats[`sales${timeFilterSuffix}`]}
                      </TableCell>
                      <TableCell className="text-center">
                        {stats[`sales${timeFilterSuffix}`] > 0
                          ? `${stats[`turnover${timeFilterSuffix}`].toFixed(2)} €`
                          : ""}
                        {stats[`sales${timeFilterSuffix}`] > 0 ? (
                          <div className="text-xxs font-bold leading-none">
                            {stats[`sales${timeFilterSuffix}`]} sale
                            {stats[`sales${timeFilterSuffix}`] > 1 ? "s" : ""}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-center">
                        {stats[`sales${timeFilterSuffix}`] > 0
                          ? `${(
                              stats[`turnover${timeFilterSuffix}`] /
                              Math.max(stats[`sent${timeFilterSuffix}`], 1)
                            ).toFixed(2)} €`
                          : ""}
                        {stats[`sales${timeFilterSuffix}`] > 0 && (
                          <div className="text-xxs font-bold leading-none">
                            per conversation
                          </div>
                        )}
                      </TableCell>
                    </>
                  )}
                </>
              )}

              {user.customer.isExternalTurnoverSplioActivated && !isMobile && (
                <>
                  <TableCell className="text-center">
                    {stats[`externalTurnover${timeFilterSuffix}`] > 0
                      ? stats[`externalTurnover${timeFilterSuffix}`] + "€"
                      : ""}
                  </TableCell>
                  <TableCell className="text-center">
                    {stats[`externalTurnover${timeFilterSuffix}`] > 0
                      ? (
                          stats[`externalTurnover${timeFilterSuffix}`] /
                          Math.max(1, stats[`sent${timeFilterSuffix}`])
                        ).toFixed(2) + "€"
                      : ""}
                  </TableCell>
                </>
              )}

              {!isMobile && (
                <>
                  {integrations.shopify && (
                    <TableCell className="text-center">
                      {`${(
                        Math.min(
                          1,
                          stats?.[`sales${timeFilterSuffix}`] /
                            Math.max(1, stats?.[`sent${timeFilterSuffix}`]),
                        ) * 100
                      )?.toFixed(1)}%`}
                    </TableCell>
                  )}

                  <TableCell className="text-center">
                    {`${(
                      Math.min(
                        1,
                        stats?.[`optoutCount${timeFilterSuffix}`] /
                          Math.max(1, stats?.[`sent${timeFilterSuffix}`]),
                      ) * 100
                    )?.toFixed(1)}%`}
                  </TableCell>
                </>
              )}
            </TableRow>
          );
          newRows.push(row);
        }
      }

      // Tri des données après récupération
      newRows.sort((a, b) => {
        return (
          parseInt(b.props.children[1].props.children) -
          parseInt(a.props.children[1].props.children)
        );
      });

      setRows(newRows);
      setLoading(false);
    }

    fetchStats();
  }, [
    data,
    selectedDateFilter,
    user,
    startDate,
    endDate,
    integrations,
    timeFilterSuffix,
  ]);

  return (
    <>
      {loading ? (
        <tr>
          <td colSpan="100%" className="py-4">
            <LoadingSpinner />
          </td>
        </tr>
      ) : (
        rows
      )}
    </>
  );
}
